import { useCallback } from 'react'

import type { SortDescriptor } from '@heroui/react'
import { cn, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useDriversStore } from '@/store/drivers.store'
import type { Vehicle } from '@/types/vehicle.types'

import { DriverTableRow } from './DriverTableRow'

interface DriversTableProps {
  vehicles: Vehicle[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
}

function SortableColumnHeader({
                                children,
                                sortDirection,
                              }: {
  children: React.ReactNode;
  sortDirection?: 'ascending' | 'descending';
}) {
  return (
    <span className="flex items-center justify-between gap-1">
      {children}
      {!!sortDirection && (
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          className={cn(
            'transform transition-transform duration-150 ease-out',
            sortDirection === 'descending' ? 'rotate-180' : '',
          )}
        >
          <path d="M7 14l5-5 5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  )
}

export function DriversTable({
                               vehicles,
                               onEdit,
                               onDelete,
                               sortDescriptor,
                               onSortChange,
                             }: DriversTableProps) {
  const { t } = useTranslation()
  const { drivers, pagination, setPagination } = useDriversStore()

  const getVehicle = useCallback((id?: string) =>
      id ? vehicles.find(v => v.id === id) : undefined,
    [vehicles])

  const handlePageChange = useCallback((pageNumber: number) => {
    setPagination({ page: pageNumber })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const start = (pagination.page == 0 ? 0 : (pagination.page)) * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <Table>
      <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.48))]">
        <Table.Content
          aria-label="Drivers list table"
          sortDescriptor={sortDescriptor}
          onSortChange={onSortChange}
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column allowsSorting id="name">
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('drivers.full_name')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column allowsSorting id="phone">
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('drivers.phone')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column id="vehicle">
              {t('vehicles.title')}
            </Table.Column>

            <Table.Column></Table.Column>
          </Table.Header>
          <Table.Body>
            {drivers.map((d: any) => (
              <DriverTableRow
                key={d.id}
                driver={d}
                vehicle={d.assignedVehicle || getVehicle(d.assignedVehicleId)}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <div className="flex w-full items-center justify-between ">
          <div className="flex items-center gap-4">
            <RowsPerPage
              value={pagination.limit}
              onChange={handleLimitChange}
            />
            <span className="text-xs text-foreground/50">
              {t('common.pagination_summary', { start, end, total: pagination.total })}
            </span>
          </div>
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </div>
      </Table.Footer>
    </Table>
  )
}
